import { inject, Injectable } from '@angular/core';
import { LoginType } from '@app/shared/utils/enums/login-type.enum';
import {
  ConsentConfig,
  CountryConsent,
  ErrorTextsDisplayed,
} from 'src/app/core/models';
import { environment } from 'src/environments/environment';
import { AppTitle, AppType } from 'src/environments/environment.interface';
import { DefaultSocials } from './models/default-socials';
import { ClientFeatureService } from './services/client-feature.service';
import { FeatureService } from './services/feature.service';
import { InitiatorService } from './services/initiator.service';
import { isCountryConsent } from './services/utils/consents';

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  readonly isWhiteLabel = environment.isWhiteLabel;
  readonly isWhiteLabelDefault = environment.appType === AppType.WL;
  readonly isWhiteLabelMyVetReco = environment.appType === AppType.MVR;
  readonly isRoyalCanin = environment.appType === AppType.RC;
  private readonly initiatorService = inject(InitiatorService);
  private readonly featureService = inject(FeatureService);
  private readonly clientFeatureService = inject(ClientFeatureService);

  getTranslationPrefix(): 'wl.' | 'myvetreco.' | '' {
    switch (environment.appType) {
      case AppType.WL:
        return 'wl.';
      case AppType.MVR:
        return 'myvetreco.';
      default:
        return '';
    }
  }

  getWhileLabelFooter(): string | undefined {
    return environment.whiteLabelConfiguration?.footer;
  }

  getGlobalRedirectURI(): string {
    return environment.globalRedirectUri;
  }

  getAppTitle(): AppTitle {
    switch (environment.appType) {
      case AppType.WL:
        return AppTitle.WHITE_LABEL_DEFAULT;
      case AppType.MVR:
        return AppTitle.MY_VET_RECO;
      default:
        return AppTitle.ROYAL_CANIN;
    }
  }

  getCountryConsents(countryCode: string): ConsentConfig[] {
    const consents =
      this.featureService.features.countryConsents[countryCode] ||
      this.featureService.features.countryConsents['DEFAULT'];
    const clientId = this.initiatorService.clientId;
    return consents.filter((consent) => {
      if (
        !clientId ||
        !Array.isArray(consent.allowedClientIds) ||
        consent.allowedClientIds.length === 0
      ) {
        return true;
      }

      return consent.allowedClientIds.includes(clientId);
    });
  }

  getCountryConsentsWithPurposes(countryCode: string): CountryConsent[] {
    return this.getCountryConsents(countryCode).filter((consent) =>
      isCountryConsent(consent),
    ) as CountryConsent[];
  }

  getLoginType(): LoginType[] {
    return (
      this.clientFeatureService.clientFeatures.loginTypes || [LoginType.email]
    );
  }

  getUrlContactSupport(clientId?: string | null): string {
    return this.clientFeatureService.clientFeatures.contactSupportUrl || '';
  }

  getUrlCreateAccount(): string {
    return this.clientFeatureService.clientFeatures.createAccountUrl || '';
  }

  getDisplayedTextInErrorPageFromEnv(): ErrorTextsDisplayed {
    return (
      this.clientFeatureService.clientFeatures.handleDisplayedTextInErrorPage ??
      this.featureService.features.handleDisplayedTextInErrorPage
    );
  }

  isDefaultSocialsAvailable(
    countryCode: string,
    languageCode: string,
  ): boolean {
    return (
      this.isAvailableForCountry(countryCode) &&
      this.isAvailableForLanguage(languageCode) &&
      this.isAvailableForCombination(countryCode) &&
      this.isAvailableForClientId()
    );
  }

  isSocialAvailableForClientID(socialNetwork: string): boolean {
    return (
      this.clientFeatureService.clientFeatures.includedCustomSocials?.includes(
        socialNetwork,
      ) ?? false
    );
  }

  isDefaultSocialAvailableForClientID(socialNetwork: DefaultSocials): boolean {
    return !this.clientFeatureService.clientFeatures.excludedDefaultSocials?.includes(
      socialNetwork,
    );
  }

  getGdpRedirectURI() {
    return environment.gdpRedirectURI;
  }

  private isAvailableForClientId() {
    return !this.clientFeatureService.clientFeatures.excludedFromSocials?.flag;
  }

  private isAvailableForCountry(countryCode: string) {
    return !this.featureService.features.excludedFromSocials.countries.includes(
      countryCode,
    );
  }

  private isAvailableForLanguage(languageCode: string) {
    return !this.featureService.features.excludedFromSocials.languages.includes(
      languageCode,
    );
  }

  private isAvailableForCombination(countryCode: string): boolean {
    return !this.clientFeatureService.clientFeatures.excludedFromSocials?.country?.includes(
      countryCode,
    );
  }
}
